import React, { useState, useEffect } from 'react';
import { Button } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

import useProduct from '@root/resources/integrations/shopify-integration/useProduct';
import useProducts from '@root/resources/integrations/shopify-integration/useProducts';
import useCollections from '@root/resources/integrations/shopify-integration/useCollections';
import api from '@root/api/newApi';
import fileResource from '@root/resources/file';
import AuthContext from '@root/resources/auth/auth.context';
import { truncateString } from '@root/utils/formatString.util';

import ShopifyProducts from './ShopifyProducts';
import RefetchProductsText from './RefetchProductsText';
import ShopifySearchBar from './ShopifySearchBar';
import useStyles from './ShopifyProductSelect.styles';
import PageSelect from './ShopifyPageSelect';
import OptionSelect from './components/Select/OptionSelect';

const MAX_TITLE_LENGTH = 14;

const returnText = '← Return to Product Description Generator';
const headlineText = 'Select the products from your Shopify store that you would like to generate a product description for.';

const PRODUCT_STATUSES = [
  { id: 'active', title: 'Active', isSelected: true },
  { id: 'draft', title: 'Drafts', isSelected: true },
  { id: 'archived', title: 'Archived', isSelected: false },
];

const PAGE_SIZE_OPTIONS = [
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '250', value: 250 },
  { label: 'All', value: 2000 },
];

const truncateTitle = (title) => truncateString(title, MAX_TITLE_LENGTH, '...');

const ShopifyProductSelect = ({ close, documentId, reloadData }) => {
  const { currentUser } = AuthContext.useAuth();

  const [text, setText] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const classes = useStyles();
  const [pageSize, setPageSize] = useState(250);
  const [currentPage, setCurrentPage] = useState(1);

  const [chosenCollectionId, setChosenCollectionId] = useState(null);
  const [chosenProductStatusesIds, setChosenProductStatusesIds] = useState(['active', 'draft']);

  const [productFetchingRestrictions, setProductFetchingRestrictions] = useState({
    ...(chosenCollectionId && { collectionId: chosenCollectionId }),
    ...(chosenProductStatusesIds && { productStatuses: chosenProductStatusesIds }),
  });

  const {
    data: productsRequestResult, isFetching: isProductsFetching,
  } = useProducts({
    ...productFetchingRestrictions,
    ...(currentUser.featureToggles.newShopifyIntegration && { limit: true }),
  });

  const [productsWithSubstringMatch, setProductsWithSubstringMatch] = useState([]);

  const searchWithinFetchedProducts = () => {
    setProductsWithSubstringMatch(
      (productsRequestResult?.products || [])
        .filter((product) => product.title.toLowerCase().includes(text.toLowerCase())),
    );
  };

  useEffect(() => searchWithinFetchedProducts(), [text, productsRequestResult?.products]);

  const onApplyFilters = () => {
    setProductFetchingRestrictions({
      ...(chosenCollectionId && { collectionId: chosenCollectionId }),
      ...(chosenProductStatusesIds && { productStatuses: chosenProductStatusesIds }),
    });

    setCurrentPage(1);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const onChosenCollectionUpdate = (newCollection) => {
    if (newCollection) {
      setChosenCollectionId(newCollection.id);
      return;
    }

    setChosenCollectionId(null);
  };

  const onChosenProductStatusesUpdate = (newStatuses) => {
    if (newStatuses.length > 0) {
      setChosenProductStatusesIds(newStatuses.map(({ id }) => id));
      return;
    }

    setChosenProductStatusesIds(null);
  };

  const { refetch } = useProduct(documentId);
  const { data: collections, isFetching: isCollectionsFetching } = useCollections();
  const { mutateAsync: updateFile } = fileResource.useUpdateFile();

  const linkProduct = async () => {
    await api.shopify.linkProduct({
      documentId: documentId.toString(),
      productId: selectedProduct.id.toString(),
    });
    await updateFile({
      fileId: documentId,
      data: {
        data: {
          companyName: selectedProduct.vendor,
          productName: selectedProduct.title,
          ...(selectedProduct.tags && { productKeywords: selectedProduct.tags.split(',') }),
        },
      },
    });
    refetch();
    reloadData();
    close();
  };

  const optionCollections = isCollectionsFetching ? []
    : collections.map(({ id, title, productCount }) => ({ id, title: `${truncateTitle(title)} (${productCount})`, isSelected: false }));

  return (
    <div className={classes.root}>
      <Button
        className={classes.button}
        variant="text"
        onClick={close}
      >
        {returnText}
      </Button>
      <div className={classes.title}>
        {headlineText}
      </div>
      <RefetchProductsText />
      {productsRequestResult?.isLimitExceeded && (
        <div className={classes.exceedAlert}>
          <InfoIcon className={classes.exceedIcon} />
          <p className={classes.exceedText}>
            The first 2,000 products were loaded from your Shopify store.
            Update the filters and apply to return additional items.
          </p>
        </div>
      )}
      <div className={classes.overflow}>
        <div className={classes.controls}>
          <ShopifySearchBar
            query={text}
            setQuery={setText}
            resetPageNumber={() => setCurrentPage(1)}
          />
          {currentUser.featureToggles.newShopifyIntegration && (
            <div className={classes.selectorContainer}>
              <OptionSelect
                selectTitle="Collection"
                options={optionCollections}
                isLoading={isCollectionsFetching}
                alignOptionsToLeft
                onUpdate={onChosenCollectionUpdate}
              />
              <OptionSelect
                selectTitle="Product status"
                options={PRODUCT_STATUSES}
                onUpdate={onChosenProductStatusesUpdate}
                isMultiSelect
              />
              {currentUser.featureToggles.newShopifyIntegration && (
                <Button
                  variant="outlined"
                  onClick={onApplyFilters}
                  className={classes.updateButton}
                >
                  Apply filters
                </Button>
              )}
            </div>
          )}
        </div>
        <ShopifyProducts
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          pageSize={pageSize}
          currentPage={currentPage}
          products={productsWithSubstringMatch}
          isLoading={isProductsFetching}
        />
      </div>
      <div className={classes.bottomButtons}>
        <PageSelect
          pageSize={pageSize}
          currentPage={currentPage}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          setCurrentPage={setCurrentPage}
          setPageSize={onPageSizeChange}
          numberOfResults={productsWithSubstringMatch && productsWithSubstringMatch.length}
          isLoading={isProductsFetching}
        />
        <Button
          className={classes.continueButton}
          onClick={linkProduct}
          disabled={!selectedProduct}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default ShopifyProductSelect;
